import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fas } from '@fortawesome/free-solid-svg-icons';

import './StatTile.scss';

/**
 * Render a tile icon. Supports PrimeIcons ("pi pi-*") via an <i> element and
 * FontAwesome solid icons ("fa-solid fa-school" / "fa-school") via the
 * <FontAwesomeIcon> component — matching how the rest of the app renders icons
 * (no global FontAwesome webfont CSS is loaded, so raw <i class="fa-*"> would
 * not render).
 */
const TileIcon: React.FC<{ icon: string }> = ({ icon }) => {
  if (icon.includes('fa-')) {
    const name = icon
      .split(/\s+/)
      .map((t) => t.trim())
      .filter((t) => t.startsWith('fa-'))
      .map((t) => t.replace(/^fa-/, ''))
      .filter((t) => !['solid', 'regular', 'brands', 'light'].includes(t))
      .pop();
    const def = name
      ? fas[
          `fa${name
            .split('-')
            .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
            .join('')}` as keyof typeof fas
        ]
      : undefined;
    if (def) return <FontAwesomeIcon icon={def} />;
  }
  return <i className={icon} />;
};

export interface StatTileProps {
  /** Big value, e.g. "59,312" or "1.22 Cr". */
  value: string;
  /** Small caption under the value, e.g. "59,312". */
  subValue?: string;
  /** Label under the value, e.g. "TOTAL SCHOOLS". */
  label: string;
  /** PrimeIcons class for the leading icon (e.g. 'pi pi-building'). */
  icon?: string;
  /**
   * Visual variant. 'solid' uses a filled gradient (highlighted tile),
   * 'default' uses the standard dark KPI tile.
   */
  variant?: 'default' | 'solid';
  /** Optional footer note (used by the highlighted tile). */
  note?: string;
  /** Extra class names. */
  className?: string;
  /** Click handler (optional). */
  onClick?: () => void;
}

/**
 * A reusable gradient KPI tile used in the dashboard stats row.
 *
 * @example
 * <StatTile icon="pi pi-building" value="59,312" subValue="59,312"
 *   label="TOTAL SCHOOLS" />
 */
const StatTile: React.FC<StatTileProps> = ({
  value,
  subValue,
  label,
  icon,
  variant = 'default',
  note,
  className = '',
  onClick,
}) => {
  return (
    <div
      className={`stat-tile stat-tile--${variant} ${className}`.trim()}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {icon && (
        <span className="stat-tile__icon">
          <TileIcon icon={icon} />
        </span>
      )}
      <div className="stat-tile__content">
        <span className="stat-tile__value">{value}</span>
        {subValue && <span className="stat-tile__sub">{subValue}</span>}
        <span className="stat-tile__label">{label}</span>
        {note && <span className="stat-tile__note">{note}</span>}
      </div>
    </div>
  );
};

export default StatTile;
